import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Descriptions, Empty, Space, Spin, Table, Tag, Typography, message
} from 'antd';
import { ArrowLeftOutlined, CarOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  supabase,
  getCurrentOrgId,
  mapJobToClientRequestStatus,
  getClientRequestStatusColor,
  isClientRequestOverdueUnscheduled,
  parseClientRequestNotes,
  type Job,
  type JobLeg,
} from '@ubus/shared';
import { translateClientRequestStatus } from '../utils/clientStatusLabels';

const { Title, Text } = Typography;

type JobRow = Job & { job_legs: JobLeg[] };

function shortPlace(address?: string | null, fallback = '—'): string {
  if (!address?.trim()) return fallback;
  return address.split(',')[0]?.trim() || address.trim();
}

function RequestDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState<JobRow | null>(null);
  const [linkedTripId, setLinkedTripId] = useState<string | null>(null);
  const noData = t('common.noData');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const orgId = await getCurrentOrgId();
      if (!orgId) {
        setJob(null);
        return;
      }

      const { data, error } = await supabase
        .from('jobs')
        .select('*, job_legs(*)')
        .eq('id', id)
        .eq('org_id', orgId)
        .eq('source', 'client_request')
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setJob(null);
        setLinkedTripId(null);
        return;
      }

      const row = data as JobRow;
      row.job_legs = (row.job_legs ?? []).sort((a, b) => a.sequence - b.sequence);
      setJob(row);

      const legTrip = row.job_legs.find((l) => l.trip_id)?.trip_id ?? null;
      if (legTrip) {
        setLinkedTripId(legTrip);
      } else {
        const { data: trip } = await supabase
          .from('trips')
          .select('id')
          .eq('job_id', row.id)
          .limit(1)
          .maybeSingle();
        setLinkedTripId(trip?.id ?? null);
      }
    } catch (err) {
      console.error(err);
      message.error(t('requests.loadFailed'));
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!job) {
    return (
      <Empty description={t('requests.notFound')}>
        <Button type="primary" onClick={() => navigate('/requests')}>
          {t('requests.backToRequests')}
        </Button>
      </Empty>
    );
  }

  const status = mapJobToClientRequestStatus(job);
  const leg = job.job_legs?.[0];
  const orderNotes = job.notes?.trim() || null;
  const legParsed = parseClientRequestNotes(leg?.notes);
  const legacyParsed = parseClientRequestNotes(job.notes);
  const vehiclePreference = legParsed.vehiclePreference || legacyParsed.vehiclePreference;
  const vehiclePreferenceLabel = (() => {
    if (!vehiclePreference) return null;
    const map: Record<string, string> = {
      standard: t('booking.vehicleStandard'),
      mini: t('booking.vehicleMini'),
      coach: t('booking.vehicleCoach'),
      any: t('booking.vehicleAny'),
    };
    return map[vehiclePreference] ?? vehiclePreference;
  })();
  const passengerGroups =
    legParsed.passengerGroups.length > 0
      ? legParsed.passengerGroups
      : legacyParsed.passengerGroups;
  const legNotesText = legParsed.specialNotes;
  const pickupTime = leg?.earliest_start ? leg.earliest_start.slice(0, 5) : noData;

  return (
    <div>
      <div className="client-page-header">
        <div>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/requests')}
            style={{ marginLeft: -8, marginBottom: 4 }}
          >
            {t('nav.requests')}
          </Button>
          <Title level={3} className="client-page-title" style={{ marginBottom: 4 }}>
            {t('requests.detailTitle')}
          </Title>
          <Space size={8} wrap>
            <Tag color={getClientRequestStatusColor(status)}>
              {translateClientRequestStatus(t, status)}
            </Tag>
            {isClientRequestOverdueUnscheduled(job) ? (
              <Tag color="error">{t('requests.overdueBadge')}</Tag>
            ) : null}
            <Text type="secondary">
              {dayjs(job.schedule_date).format('DD MMM YYYY')}
              {pickupTime !== noData ? ` · ${pickupTime}` : ''}
            </Text>
          </Space>
        </div>
        <div className="client-actions">
          {linkedTripId && status === 'scheduled' && (
            <Button
              type="primary"
              icon={<CarOutlined />}
              onClick={() => navigate(`/trip/${linkedTripId}`)}
            >
              {t('requests.openTrip')}
            </Button>
          )}
        </div>
      </div>

      {status === 'rejected' && (
        <Alert
          type="error"
          showIcon
          message={t('requests.rejectedAlert')}
          description={job.rejection_reason || t('requests.rejectedDefaultReason')}
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      {status === 'submitted' && (
        <Alert
          type="info"
          showIcon
          message={t('requests.submittedAlert')}
          description={t('requests.submittedAlertDesc')}
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      {status === 'accepted' && (
        <Alert
          type="warning"
          showIcon
          message={t('requests.acceptedAlert')}
          description={t('requests.acceptedAlertDesc')}
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      {isClientRequestOverdueUnscheduled(job) && (
        <Alert
          type="error"
          showIcon
          message={t('requests.overdueAlert')}
          description={t('requests.overdueAlertDesc')}
          style={{ marginBottom: 20, borderRadius: 10 }}
        />
      )}

      <Descriptions
        bordered
        size="middle"
        column={1}
        style={{ marginBottom: 24 }}
        labelStyle={{ width: 160 }}
      >
        <Descriptions.Item label={t('common.pickup')}>
          {leg?.pickup_address || noData}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.dropoff')}>
          {leg?.dropoff_address || noData}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.route')}>
          {leg?.pickup_address && leg?.dropoff_address
            ? `${shortPlace(leg.pickup_address, noData)} → ${shortPlace(leg.dropoff_address, noData)}`
            : noData}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.passengers')}>
          {job.passenger_count ?? noData}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.contact')}>
          {job.contact_person?.trim() || noData}
        </Descriptions.Item>
        <Descriptions.Item label={t('common.phone')}>
          {job.contact_phone?.trim() || noData}
        </Descriptions.Item>
        <Descriptions.Item label={t('requests.vehiclePreference')}>
          {vehiclePreferenceLabel || noData}
        </Descriptions.Item>
        <Descriptions.Item label={t('requests.specialNotes')}>
          {orderNotes || noData}
        </Descriptions.Item>
        <Descriptions.Item label={t('requests.legNotes')}>
          {legNotesText || noData}
        </Descriptions.Item>
      </Descriptions>

      {passengerGroups.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Text
            strong
            style={{
              display: 'block',
              marginBottom: 10,
              fontSize: 12,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--client-ink-secondary)',
            }}
          >
            {t('requests.passengerGroups')}
          </Text>
          <Table
            size="small"
            pagination={false}
            rowKey={(_, i) => String(i)}
            dataSource={passengerGroups}
            columns={[
              { title: t('requests.groupColumn'), dataIndex: 'name' },
              { title: t('requests.countColumn'), dataIndex: 'count', width: 80, align: 'center' },
              { title: t('requests.groupLeadColumn'), dataIndex: 'lead', render: (v) => v || noData },
              { title: t('requests.phoneColumn'), dataIndex: 'phone', render: (v) => v || noData },
            ]}
          />
        </div>
      )}

      {(legParsed.rawNotes || legacyParsed.rawNotes) && passengerGroups.length === 0 && (
        <Alert
          type="info"
          showIcon
          message={t('requests.additionalNotes')}
          description={
            <Text style={{ whiteSpace: 'pre-wrap' }}>
              {legParsed.rawNotes || legacyParsed.rawNotes}
            </Text>
          }
        />
      )}
    </div>
  );
}

export default RequestDetail;
